<template>
  <!-- Q3 "Workload" from the Work Progress design: tasks completed per period (look back, green)
       against open tasks due per period (look ahead, blue), centred on today. Sync only. -->
  <v-card class="workload-card">
    <div class="head">
      <h2 class="card-title">{{ $t('taskWorkload.title') }}</h2>
      <v-btn-toggle
        v-model="weeks"
        mandatory
        density="compact"
        color="info"
        variant="tonal"
        class="seg"
      >
        <v-btn v-for="n in [1, 2, 3, 4]" :key="n" :value="n" size="small" class="seg-btn">
          {{ $t('taskWorkload.weekBtn', { n }) }}
        </v-btn>
      </v-btn-toggle>
    </div>

    <v-skeleton-loader v-if="!bars.length && loading" type="image" height="150" />

    <template v-else>
      <div class="counts">
        <span><strong class="tk-ok">{{ pastN.toLocaleString() }}</strong> {{ $t('taskWorkload.doneSuffix', { span }) }}</span>
        <span class="text-right"><strong class="tk-info">{{ nextN.toLocaleString() }}</strong> {{ $t('taskWorkload.dueSuffix', { span }) }}</span>
      </div>

      <svg viewBox="0 0 600 150" width="100%" role="img" class="chart">
        <line x1="300" y1="6" x2="300" y2="120" stroke="#F5A623" stroke-width="1.2" stroke-dasharray="4 3" />
        <text x="300" y="131" font-size="10" text-anchor="middle" fill="#F5A623">{{ $t('taskSCurve.today') }}</text>
        <line x1="6" y1="120" x2="594" y2="120" class="axis" />
        <g
          v-for="bar in bars"
          :key="bar.key"
          class="bar"
          :class="{ 'is-selected': bar.selected, 'is-dimmed': selected && !bar.selected, 'is-empty': bar.count === 0 }"
          :role="bar.count ? 'button' : undefined"
          :tabindex="bar.count ? 0 : -1"
          :aria-pressed="bar.count ? bar.selected : undefined"
          @click="pick(bar)"
          @keydown.enter.prevent="pick(bar)"
        >
          <!-- a full-height hit area so thin bars are easy to click -->
          <rect :x="bar.hx" y="6" :width="bar.hw" height="136" fill="transparent" />
          <rect :x="bar.x" :y="bar.y" :width="bar.w" :height="bar.h" rx="2" :fill="bar.fill" />
          <text :x="bar.cx" :y="bar.ty" font-size="11" font-weight="500" text-anchor="middle" class="bar-n">{{ bar.n }}</text>
          <text :x="bar.cx" y="140" font-size="10" text-anchor="middle" class="bar-label">{{ bar.label }}</text>
        </g>
      </svg>

      <div class="verdict" :class="verdict.cls">{{ verdict.text }}</div>
    </template>
  </v-card>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { workloadSql } from '@/utils/taskTables';
import { taskSCurveFilters, taskScopeKey, taskScope, isTaskScopeReady, toggleWorkloadFilter } from '@/utils/taskSCurveFilters';
import { startOfToday, addDays, formatWeekday } from '@/utils/taskDates';

const store = useStore();
const { t } = useI18n();
const conn = computed(() => store.getters.getDBConnection);
const dataLoaded = computed(() => store.getters.getDataLoaded);
const locale = computed(() => store.getters.currentLocale);

// Window on each side of today: 1 week → 7 daily bins; 2–4 weeks → one bin per week
const weeks = ref(2);
const daily = computed(() => weeks.value === 1);
const bins = computed(() => (daily.value ? 7 : weeks.value));
const step = computed(() => (daily.value ? 1 : 7));

const rows = ref([]);
const loading = ref(false);

let queryVersion = 0;

const queryRows = async (query) => {
  const result = await conn.value.query(query);
  return result.toArray().map(row => row.toJSON());
};

const loadData = async () => {
  if (!dataLoaded.value || !isTaskScopeReady.value) return;
  const version = ++queryVersion;
  loading.value = true;
  try {
    // Ignore the workload cross-filter here: this chart is where it is picked
    const result = await queryRows(workloadSql({
      ...taskScope(['workload']),
      step: step.value,
      bins: bins.value,
    }));
    if (version !== queryVersion) return; // a newer request superseded this one
    rows.value = result;
  } catch (error) {
    if (version !== queryVersion) return;
    console.error('[TaskWorkloadChart] query failed:', error.message);
    rows.value = [];
  } finally {
    if (version === queryVersion) loading.value = false;
  }
};

const counts = computed(() => {
  const map = {};
  for (const row of rows.value) map[`${row.side}:${Number(row.bin)}`] = Number(row.n);
  return map;
});

const pastN = computed(() => Object.entries(counts.value).filter(([k]) => k.startsWith('past')).reduce((s, [, n]) => s + n, 0));
const nextN = computed(() => Object.entries(counts.value).filter(([k]) => k.startsWith('next')).reduce((s, [, n]) => s + n, 0));

const span = computed(() =>
  daily.value ? t('taskWorkload.spanDays', { n: 7 }) : t('taskWorkload.spanWeeks', { n: weeks.value })
);

// Same layout as the design's SVG: past bars in x 6–294, future bars in x 306–594, baseline y 120
const bars = computed(() => {
  if (!dataLoaded.value || (loading.value && !rows.value.length)) return [];
  const today = startOfToday();
  const n = bins.value;
  const list = [];
  for (let i = n - 1; i >= 0; i--) {
    list.push({
      key: `past-${i}`,
      side: 'past',
      bin: i,
      n: counts.value[`past:${i}`] ?? 0,
      past: true,
      label: daily.value ? formatWeekday(addDays(today, -i), locale.value) : t('taskWorkload.wkPast', { n: i + 1 }),
    });
  }
  for (let i = 0; i < n; i++) {
    list.push({
      key: `next-${i}`,
      side: 'next',
      bin: i,
      n: counts.value[`next:${i}`] ?? 0,
      past: false,
      label: daily.value ? formatWeekday(addDays(today, i + 1), locale.value) : t('taskWorkload.wkNext', { n: i + 1 }),
    });
  }
  const max = Math.max(1, ...list.map(b => b.n));
  const slotW = 288 / n;
  const barW = Math.min(44, slotW * 0.62);
  return list.map((b, i) => {
    const idx = i < n ? i : i - n;
    const cx = (i < n ? 6 : 306) + idx * slotW + slotW / 2;
    const h = b.n / max * 92;
    const y = 120 - h;
    return {
      ...b,
      selected: isSelected(b),
      hx: (cx - slotW / 2).toFixed(1),
      hw: slotW.toFixed(1),
      x: (cx - barW / 2).toFixed(1),
      y: y.toFixed(1),
      w: barW.toFixed(1),
      h: Math.max(h, 1.5).toFixed(1),
      cx: cx.toFixed(1),
      ty: (y - 4).toFixed(1),
      count: b.n,
      n: b.n.toLocaleString(),
      fill: b.past ? '#4CAF50' : '#2196F3',
    };
  });
});

// Clicking a bar cross-filters the other visuals to the tasks in that period
const selected = computed(() => taskSCurveFilters.workload);
const isSelected = (b) => !!selected.value && selected.value.side === b.side && selected.value.bin === b.bin && selected.value.step === step.value;
// An empty period has nothing to filter to, so clicking it does nothing
const pick = (bar) => { if (bar.count > 0) toggleWorkloadFilter({ side: bar.side, bin: bar.bin, step: step.value }); };

const verdict = computed(() => {
  if (nextN.value > pastN.value) {
    const pct = Math.round(nextN.value / Math.max(1, pastN.value) * 100 - 100);
    return { text: t('taskWorkload.verdictAbove', { pct }), cls: 'tk-warn' };
  }
  if (nextN.value < pastN.value) return { text: t('taskWorkload.verdictBelow'), cls: 'tk-ok' };
  return { text: t('taskWorkload.verdictMatch'), cls: 'tk-ok' };
});

onMounted(loadData);
watch(
  [dataLoaded, weeks, taskScopeKey],
  loadData,
  { deep: true }
);
</script>

<style scoped>
.workload-card {
  min-width: 0;
  width: 100%;
  margin: 0; /* App.vue gives every v-card margin: 0 auto, which shrinks a grid item to its content */
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 20px 14px;
}
.head {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}
.card-title {
  margin: 0;
  font-size: 20px;
  line-height: 28px;
  font-weight: 400;
}
.seg {
  height: 30px;
}
.seg-btn {
  text-transform: none;
  letter-spacing: 0;
  font-size: 12px;
}
.counts {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  opacity: 0.85;
}
.counts strong {
  font-size: 18px;
  font-weight: 400;
}
.chart {
  display: block;
  flex: 1 1 auto;
}
.axis {
  stroke: currentColor;
  stroke-opacity: 0.2;
  stroke-width: 1;
}
.bar {
  cursor: pointer;
  outline: none;
  transition: opacity 0.2s ease;
}
.bar:hover rect:last-of-type,
.bar.is-selected rect:last-of-type {
  stroke: var(--tk-info);
  stroke-width: 2;
}
.bar.is-dimmed {
  opacity: 0.4;
}
.bar.is-empty {
  cursor: default;
}
.bar.is-empty:hover rect:last-of-type {
  stroke: none;
}
.bar-n {
  fill: currentColor;
}
.bar-label {
  fill: currentColor;
  fill-opacity: 0.6;
}
.verdict {
  font-size: 12px;
}
</style>
